import { hasVersionHistory } from "@/domains/documents/utils/versionHistory";
import {
  type DocumentMenuConfig,
  type FolderMenuConfig,
} from "@/domains/documents/components/organisms/DocumentRowContextMenu";
import { verifiedSelectionState } from "@/domains/documents/hooks/useDocumentVerification";
import { isSystemFolder } from "@/domains/documents/services/folderService";
import type { TreeFolder } from "@/domains/documents/types";
import { isBriefFolder } from "@/domains/documents/utils/briefFolder";
import type { DocumentWithTags } from "./documentTableTypes";
import { verifyMarks } from "./documentTableValues";

import type { createTableBulkActions } from "./createTableBulkActions";
import type { createTableDocumentActions } from "./createTableDocumentActions";
import type { documentTableOptions } from "./documentTableOptions";
import type { useTableBasics } from "./useTableBasics";
import type { useTableComments } from "./useTableComments";
import type { useTableCompare } from "./useTableCompare";
import type { useTableDialogState } from "./useTableDialogState";
import type { useTableSigning } from "./useTableSigning";
type Options = Pick<
  ReturnType<typeof documentTableOptions>,
  | "isRestore"
  | "onRenameDocumentSubmit"
  | "canRenameDocument"
  | "selectedFolderIds"
  | "onShare"
  | "onMoveCopy"
  | "canMoveToTrash"
  | "onAddNewVersions"
  | "onCreateSubfolder"
  | "onExportFolder"
  | "onOrganizeFolder"
  | "onShareFolder"
  | "onRenameFolder"
  | "onDeleteFolder"
> &
  Pick<ReturnType<typeof useTableSigning>, "allDocuments"> &
  Pick<
    ReturnType<typeof useTableComments>,
    "canVerifyDocument" | "setVerified" | "canOpenComments" | "openComments"
  > &
  Pick<
    ReturnType<typeof useTableCompare>,
    | "canComparePdfs"
    | "canCompareIfcs"
    | "canCompareDrawings"
    | "handleDiffOverview"
    | "handleCompareSelectedDrawings"
    | "canUseDrawingViewer"
    | "openDrawingViewer"
  > &
  Pick<
    ReturnType<typeof createTableBulkActions>,
    | "runBulkDownload"
    | "handleAskFranklinClick"
    | "hostActionOnSelection"
    | "handleComparePdfs"
    | "runBulkDelete"
  > &
  Pick<
    ReturnType<typeof createTableDocumentActions>,
    "handleDownloadDocument" | "handleDeleteDocument"
  > & {
    store?: Pick<
      NonNullable<ReturnType<typeof documentTableOptions>["store"]>,
      "selectedCount" | "isSelected" | "selectDocument"
    >;
    documentRename: Pick<ReturnType<typeof useTableBasics>["documentRename"], "startRename">;
    folderRename: Pick<ReturnType<typeof useTableBasics>["folderRename"], "startRename">;
    addVersions: Pick<ReturnType<typeof useTableDialogState>["addVersions"], "openFor">;
  };
type MenuTarget = ReturnType<typeof documentMenuTarget>;

function selectedMenuDocument(options: Options, doc: DocumentWithTags, count: number) {
  const { store, allDocuments } = options;
  return count === 1 && store && !store.isSelected(doc.id)
    ? (allDocuments.find((candidate) => store.isSelected(candidate.id)) ?? doc)
    : doc;
}

function mayRenameMenuDocument(options: Options, doc: DocumentWithTags, isMulti: boolean) {
  return (
    !isMulti && !!options.onRenameDocumentSubmit && (options.canRenameDocument?.(doc) ?? false)
  );
}

function documentMenuTarget(options: Options, doc: DocumentWithTags) {
  const { store } = options;
  const selCount = store?.selectedCount ?? 0;
  const isMulti = selCount > 1;
  // A single checked row wins over the hovered row; existing selection is retained.
  const targetDoc = selectedMenuDocument(options, doc, selCount);
  const ensureSingle = () => {
    if (!store || isMulti || selCount === 1) return;
    store.selectDocument(doc.id, true);
  };
  return {
    isMulti,
    targetDoc,
    ensureSingle,
    canRename: mayRenameMenuDocument(options, targetDoc, isMulti),
  };
}

function verifiedTargets(options: Options, doc: DocumentWithTags) {
  const selected = options.store
    ? options.allDocuments.filter((row) => options.store.isSelected(row.id))
    : [];
  return selected.length > 1 ? selected : [doc];
}

function verifiedMenuAction(options: Options, doc: DocumentWithTags) {
  // Every selected document must be writable; container files remain included.
  if (options.isRestore) return { onToggleVerified: undefined, verifiedState: "none" as const };
  const targets = verifiedTargets(options, doc);
  if (targets.length === 0 || !targets.every((row) => options.canVerifyDocument(row))) {
    return { onToggleVerified: undefined, verifiedState: "none" as const };
  }
  const state = verifiedSelectionState(targets);
  return {
    verifiedState: state,
    onToggleVerified: () => void options.setVerified(verifyMarks(targets), state !== "all"),
  };
}

function selectedMenuActions(
  options: Options,
  target: MenuTarget
): Pick<DocumentMenuConfig, "onShare" | "onMoveCopy" | "onAskFranklin" | "hostAction"> {
  const { onShare, onMoveCopy, handleAskFranklinClick, hostActionOnSelection } = options;
  const { ensureSingle } = target;
  return {
    onShare: onShare
      ? () => {
          ensureSingle();
          onShare();
        }
      : undefined,
    onMoveCopy: onMoveCopy
      ? () => {
          ensureSingle();
          onMoveCopy();
        }
      : undefined,
    onAskFranklin: handleAskFranklinClick
      ? () => {
          ensureSingle();
          handleAskFranklinClick();
        }
      : undefined,
    hostAction: hostActionOnSelection
      ? {
          ...hostActionOnSelection,
          onClick: () => {
            ensureSingle();
            hostActionOnSelection.onClick();
          },
        }
      : undefined,
  };
}

function drawingComparisonAvailable(options: Options, target: MenuTarget) {
  return (
    target.isMulti &&
    options.canCompareDrawings &&
    options.selectedFolderIds.length === 0 &&
    !options.isRestore
  );
}

function menuPresentation(options: Options, target: MenuTarget) {
  return {
    isMulti: target.isMulti,
    selectedCount: options.store ? options.store.selectedCount : 1,
    filename: target.isMulti ? undefined : target.targetDoc.filename,
    isDwg: !!target.targetDoc.filename?.toLowerCase().endsWith(".dwg"),
    canCompare: target.isMulti && options.canComparePdfs,
    canCompareModels: target.isMulti && options.canCompareIfcs,
    canCompareDrawings: drawingComparisonAvailable(options, target),
  };
}

function menuFileActions(
  options: Options,
  target: MenuTarget
): Pick<
  DocumentMenuConfig,
  "onDownload" | "onDelete" | "onRename" | "onAddNewVersion" | "opensVersioning" | "onComments"
> {
  const { isMulti, targetDoc, canRename } = target;
  return {
    onDownload: () => {
      if (isMulti) options.runBulkDownload();
      else options.handleDownloadDocument(targetDoc.id);
    },
    onDelete: options.canMoveToTrash
      ? () => {
          if (isMulti) options.runBulkDelete();
          else options.handleDeleteDocument(targetDoc.id);
        }
      : undefined,
    onRename: canRename
      ? () => options.documentRename.startRename({ id: targetDoc.id, filename: targetDoc.filename })
      : undefined,
    onAddNewVersion:
      !isMulti && options.onAddNewVersions
        ? () => options.addVersions.openFor(targetDoc)
        : undefined,
    opensVersioning: hasVersionHistory(targetDoc),
    onComments:
      !isMulti && options.canOpenComments ? () => options.openComments(targetDoc) : undefined,
  };
}

function documentMenuConfig(options: Options, doc: DocumentWithTags): DocumentMenuConfig {
  const target = documentMenuTarget(options, doc);
  return {
    ...menuPresentation(options, target),
    ...selectedMenuActions(options, target),
    onCompare: options.handleComparePdfs,
    onDiffOverview: options.handleDiffOverview,
    onCompareDrawings: options.handleCompareSelectedDrawings,
    onViewDrawing:
      !options.isRestore && options.canUseDrawingViewer
        ? () => options.openDrawingViewer([target.targetDoc.id])
        : undefined,
    ...menuFileActions(options, target),
    ...verifiedMenuAction(options, target.targetDoc),
  };
}

function folderExternalActions(
  options: Options,
  folder: TreeFolder,
  brief: boolean
): Pick<FolderMenuConfig, "onCreateSubfolder" | "onExport" | "onOrganize" | "onShare"> {
  const { onCreateSubfolder, onExportFolder, onOrganizeFolder, onShareFolder } = options;
  return {
    onCreateSubfolder: onCreateSubfolder ? () => onCreateSubfolder(folder.id) : undefined,
    onExport: onExportFolder ? () => onExportFolder(folder.id) : undefined,
    onOrganize:
      onOrganizeFolder && !isSystemFolder(folder) && !brief
        ? () => onOrganizeFolder(folder.id)
        : undefined,
    onShare: onShareFolder ? () => onShareFolder(folder.id) : undefined,
  };
}

function folderMenuConfig(options: Options, folder: TreeFolder): FolderMenuConfig {
  const brief = isBriefFolder(folder);
  // The database refuses rename, delete and organization of the Brief folder.
  return {
    folderName: folder.displayName ?? folder.name,
    ...folderExternalActions(options, folder, brief),
    onRename:
      options.onRenameFolder && !brief ? () => options.folderRename.startRename(folder) : undefined,
    onDelete:
      options.onDeleteFolder && !brief ? () => options.onDeleteFolder(folder.id) : undefined,
  };
}

export function createTableMenuActions(options: Options) {
  return {
    buildDocMenuConfig: (doc: DocumentWithTags) => documentMenuConfig(options, doc),
    buildFolderMenuConfig: (folder: TreeFolder) => folderMenuConfig(options, folder),
  };
}
